import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  RefreshControl,
  Modal,
  TextInput,
  Image,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useArticles } from '../contexts/ArticleContext';
import { getStores } from '../services/storesService';
import { selectAndUploadImage } from '../services/uploadService';
import { getCollections, getCollection } from '../services/collectionsService';
// import { addItemToList } from '../services/shoppingService'; // Removed - using dynamic import instead

const QuickListScreen = ({ navigation }) => {
  const { articles, loading, loadArticles, updateArticle, addArticle } = useArticles();

  // Hilfsfunktion: Formatiert quantity (entfernt .00 bei reinen Zahlen, lässt Text unverändert)
  const formatQuantity = (quantity) => {
    if (!quantity && quantity !== 0) return '1';
    
    const quantityStr = String(quantity);
    
    // Prüfe ob es eine reine Zahl ist (nur Ziffern, optional mit . und Nachkommastellen)
    // WICHTIG: /^\d+(\.\d+)?$/ prüft ob der String nur eine Zahl ist, nicht z.B. "1-2" oder "ca. 500"
    if (/^\d+(\.\d+)?$/.test(quantityStr.trim())) {
      const numValue = parseFloat(quantityStr);
      // Wenn es eine ganze Zahl ist (oder .00 am Ende hat), zeige nur die ganze Zahl
      if (numValue % 1 === 0) {
        return String(Math.floor(numValue));
      }
      // Falls es echte Nachkommastellen hat (z.B. 1.5), zeige sie an
      return String(numValue);
    }
    
    // Ansonsten: Text unverändert zurückgeben (z.B. "1-2", "ca. 500g")
    return quantityStr;
  };
  const [stores, setStores] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [addedItems, setAddedItems] = useState(new Set());
  const [itemsInShoppingLists, setItemsInShoppingLists] = useState(new Set()); // Artikel die in Einkaufslisten sind
  const [articlePurchaseCounts, setArticlePurchaseCounts] = useState({}); // {articleId: totalPurchaseCount}
  const [articleUnits, setArticleUnits] = useState({}); // {articleId: mostRecentUnit}
  const [lastUsedStores, setLastUsedStores] = useState({}); // {articleId: storeId}
  const [customQuantities, setCustomQuantities] = useState({}); // {articleId: quantity}
  const [defaultStore, setDefaultStore] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null); // {id, storeId, quantity, tags, notes, imageUrl, uploadingImage}
  const [newTagText, setNewTagText] = useState(''); // Für das Tag-Eingabefeld
  const [searchQuery, setSearchQuery] = useState('');
  const [historicalDates, setHistoricalDates] = useState([]); // [{purchase_date, item_count}, ...]
  const [selectedHistoryDate, setSelectedHistoryDate] = useState(null); // YYYY-MM-DD oder null
  const [historicalArticleIds, setHistoricalArticleIds] = useState([]); // Artikel-IDs des gewählten Datums
  const [showImageModal, setShowImageModal] = useState(false); // Für Bildanzeige
  const [selectedImageUrl, setSelectedImageUrl] = useState(''); // URL des anzuzeigenden Bildes
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false); // Dropdown anzeigen
  const [showStoreModal, setShowStoreModal] = useState(false); // Store-Auswahl Modal
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showCollectionsDropdown, setShowCollectionsDropdown] = useState(false);
  const [collectionArticles, setCollectionArticles] = useState([]);

  // Schließe Dropdowns wenn außerhalb geklickt wird (nur Web)
  useEffect(() => {
    if ((showHistoryDropdown || showCollectionsDropdown) && typeof window !== 'undefined') {
      const handleClickOutside = () => {
        setShowHistoryDropdown(false);
        setShowCollectionsDropdown(false);
      };
      
      // Kleine Verzögerung, damit der Button-Klick nicht sofort das Dropdown schließt
      setTimeout(() => {
        window.addEventListener('click', handleClickOutside);
      }, 100);
      
      return () => {
        window.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showHistoryDropdown, showCollectionsDropdown]);

  // Collections laden
  const loadCollections = async () => {
    try {
      const result = await getCollections();
      if (result.success) {
        setCollections(result.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Collections:', error);
    }
  };

  // Collection-Artikel laden
  const loadCollectionArticles = async (collectionId) => {
    try {
      const result = await getCollection(collectionId);
      if (result.success) {
        const articleIds = result.data.items.map(item => item.article_id);
        setCollectionArticles(articleIds);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Collection-Artikel:', error);
    }
  };

  // Laden des zuletzt verwendeten Ladens pro Artikel
  const loadLastUsedStores = async () => {
    try {
      const storesData = await AsyncStorage.getItem('lastUsedStoresPerArticle');
      if (storesData) {
        return JSON.parse(storesData);
      }
    } catch (error) {
      console.error('Error loading last used stores:', error);
    }
    return {};
  };

  // Speichern des zuletzt verwendeten Ladens für einen Artikel
  const saveLastUsedStoreForArticle = async (articleId, storeId) => {
    try {
      const currentStores = await loadLastUsedStores();
      const updatedStores = {
        ...currentStores,
        [articleId]: storeId
      };
      await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(updatedStores));
      setLastUsedStores(updatedStores);
    } catch (error) {
      console.error('Error saving last used store for article:', error);
    }
  };

  // Laden der benutzerdefinierten Mengen
  const loadCustomQuantities = async () => {
    try {
      const quantitiesData = await AsyncStorage.getItem('customQuantitiesPerArticle');
      if (quantitiesData) {
        return JSON.parse(quantitiesData);
      }
    } catch (error) {
      console.error('Error loading custom quantities:', error);
    }
    return {};
  };

  // Speichern der benutzerdefinierten Menge für einen Artikel
  const saveCustomQuantityForArticle = async (articleId, quantity) => {
    try {
      const currentQuantities = await loadCustomQuantities();
      const updatedQuantities = {
        ...currentQuantities,
        [articleId]: quantity
      };
      await AsyncStorage.setItem('customQuantitiesPerArticle', JSON.stringify(updatedQuantities));
      setCustomQuantities(updatedQuantities);
    } catch (error) {
      console.error('Error saving custom quantity for article:', error);
    }
  };

  // Laden der aktuellen Einkaufslisten und prüfen welche Artikel bereits enthalten sind
  const loadItemsInShoppingLists = async () => {
    try {
      const { getAllShoppingLists } = await import('../services/shoppingService');
      const result = await getAllShoppingLists();
      
      if (result.success) {
        const articleIds = new Set();
        const articleStoreMap = {}; // {articleId: [storeId1, storeId2, ...]}
        const articlePurchaseCounts = {}; // {articleId: totalPurchaseCount}
        const articleUnits = {}; // {articleId: mostRecentUnit}
        const articleStores = {}; // {articleId: storeId} - der aktuelle Laden aus der Einkaufsliste
        const articleQuantities = {}; // {articleId: quantity} - die aktuelle Menge aus der Einkaufsliste
        
        result.data.forEach(store => {
          if (store.items) {
            store.items.forEach(item => {
              articleIds.add(item.article_id);
              
              // Sammle alle Läden für jeden Artikel
              if (!articleStoreMap[item.article_id]) {
                articleStoreMap[item.article_id] = [];
              }
              if (!articleStoreMap[item.article_id].includes(store.id)) {
                articleStoreMap[item.article_id].push(store.id);
              }
              
              // Speichere den aktuellen Laden für jeden Artikel (überschreibt bei mehreren Läden)
              articleStores[item.article_id] = store.id;
              
              // Sammle die aktuelle Menge für jeden Artikel (überschreibt bei mehreren Läden)
              articleQuantities[item.article_id] = item.quantity;
              
              // Sammle Häufigkeiten (jeder Einkauf zählt, unabhängig von Menge)
              if (!articlePurchaseCounts[item.article_id]) {
                articlePurchaseCounts[item.article_id] = 0;
              }
              // Jeder Eintrag in einer Einkaufsliste zählt als ein Einkauf
              articlePurchaseCounts[item.article_id] += 1;
              
              // Sammle die neueste Einheit für jeden Artikel
              if (item.unit) {
                articleUnits[item.article_id] = item.unit;
              }
            });
          }
        });
        
        setItemsInShoppingLists(articleIds);
        setArticlePurchaseCounts(articlePurchaseCounts);
        setArticleUnits(articleUnits);
        
        // WICHTIG: Die Einkaufslisten-Daten sind NUR relevant für Artikel, die aktuell in der Einkaufsliste sind!
        // Für alle anderen Artikel verwenden wir die Backend-Daten (item.last_used_store_id, item.default_quantity)
        // Diese Daten sind NICHT die "Single Source of Truth" - das Backend ist es!
        
        // Aktualisiere lastUsedStores mit den aktuellen Läden aus der Einkaufsliste
        setLastUsedStores(prevStores => ({
          ...prevStores,
          ...articleStores
        }));
        
        // Aktualisiere customQuantities mit den aktuellen Mengen aus der Einkaufsliste
        setCustomQuantities(prevQuantities => ({
          ...prevQuantities,
          ...articleQuantities
        }));
      }
    } catch (error) {
      console.error('Error loading items in shopping lists:', error);
    }
  };

  // Historische Einkaufstage laden
  const loadHistoricalDates = async () => {
    try {
      const { getHistoricalPurchaseDates } = await import('../services/shoppingService');
      const result = await getHistoricalPurchaseDates();
      if (result.success) {
        setHistoricalDates(result.data);
      }
    } catch (error) {
      console.error('Error loading historical dates:', error);
    }
  };

  // Artikel eines historischen Datums laden
  const loadHistoricalArticles = async (date) => {
    try {
      const { getArticlesByHistoricalDate } = await import('../services/shoppingService');
      const result = await getArticlesByHistoricalDate(date);
      if (result.success) {
        setHistoricalArticleIds(result.data);
      }
    } catch (error) {
      console.error('Error loading historical articles:', error);
    }
  };

  // EINZIGER useFocusEffect - lädt ALLE Daten bei jedem Fokus neu
  useFocusEffect(
    useCallback(() => {
      // WICHTIG: Artikel explizit neu laden, um Backend-Updates (last_used_store_id, default_quantity) zu bekommen
      loadArticles().then(() => {
        // Lade restliche Daten nach Artikel-Reload
        loadData();
        loadHistoricalDates();
        loadCollections();
      });
    }, []) // Leere Dependencies = läuft JEDES MAL wenn Screen fokussiert wird
  );

  const loadData = async () => {
    try {
      // Artikel und Läden parallel laden
      const [articlesResult, storesResult, lastUsedStoresData, customQuantitiesData] = await Promise.all([
        loadArticles(),
        getStores(),
        loadLastUsedStores(),
        loadCustomQuantities()
      ]);


      // Artikel sind bereits im Context geladen

      if (storesResult.success) {
        setStores(storesResult.data);
        // Setze zunächst die AsyncStorage-Werte
        setLastUsedStores(lastUsedStoresData);
        setCustomQuantities(customQuantitiesData);
        
        // Einkaufslisten laden und Laden-Zuordnungen aktualisieren
        // (überschreibt AsyncStorage-Werte mit aktuellen Daten aus der Einkaufsliste)
        await loadItemsInShoppingLists();
        
        // Standard-Laden setzen (erster Laden als Fallback)
        if (storesResult.data.length > 0) {
          setDefaultStore(storesResult.data[0]);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      Alert.alert('Fehler', 'Fehler beim Laden der Daten');
    } finally {
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  // Neuen Artikel aus Suchbegriff erstellen - Modal öffnen
  const createArticleFromSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    // Öffne das Modal mit vorausgefüllten Daten für einen neuen Artikel
    setEditingArticle({
      id: null, // null = neuer Artikel
      name: searchQuery.trim(),
      quantity: 1,
      unit: 'Stück',
      tags: [],
      notes: '',
      storeId: defaultStore?.id || null,
      isNew: true
    });
  };

  // Gefilterte Artikel basierend auf Suchanfrage, historischem Datum UND Collection
  const filteredArticles = useMemo(() => {
    // WICHTIG: Immer eine Kopie erstellen, damit das ursprüngliche Array nicht mutiert wird
    let result = [...articles];
    
    // 1. Filter nach Collection (falls ausgewählt)
    if (selectedCollection && collectionArticles.length > 0) {
      result = result.filter(article => 
        collectionArticles.includes(article.id)
      );
    }
    
    // 2. Filter nach historischem Datum (falls ausgewählt)
    if (selectedHistoryDate && historicalArticleIds.length > 0) {
      result = result.filter(article => 
        historicalArticleIds.includes(article.id)
      );
    }
    
    // 2. Filter nach Suchbegriff
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(article => {
        // Suche in Name
        if (article.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Suche in Tags
        const displayTags = article.default_tags || [];
        if (displayTags.some(tag => 
          tag.toLowerCase().includes(query)
        )) {
          return true;
        }
        
        // Suche in Notizen
        const displayNotes = article.notes || '';
        if (displayNotes.toLowerCase().includes(query)) {
          return true;
        }
        
        return false;
      });
    }
    
    // 3. Sortierung nach Häufigkeit (absteigend)
    // WICHTIG: Sortiere nach purchase_count vom Backend (kann durch Socket-Event aktualisiert werden)
    result.sort((a, b) => {
      // Verwende purchase_count vom Backend (wird beim Kaufen aktualisiert)
      const frequencyA = a.purchase_count || 0;
      const frequencyB = b.purchase_count || 0;
      
      // Wenn Häufigkeit gleich ist, alphabetisch sortieren
      if (frequencyB === frequencyA) {
        return a.name.localeCompare(b.name, 'de');
      }
      
      return frequencyB - frequencyA;
    });
    
    return result;
  }, [articles, searchQuery, selectedHistoryDate, historicalArticleIds, selectedCollection, collectionArticles]);

  // Collection-Handler
  const handleCollectionSelect = async (collection) => {
    setSelectedCollection(collection);
    setShowCollectionsDropdown(false);
    
    // Reset History-Filter wenn Collection ausgewählt wird
    setSelectedHistoryDate(null);
    setHistoricalArticleIds([]);
    setShowHistoryDropdown(false);
    
    if (collection) {
      await loadCollectionArticles(collection.id);
    } else {
      setCollectionArticles([]);
    }
  };

  // History-Handler
  const handleHistorySelect = async (date) => {
    setSelectedHistoryDate(date);
    setShowHistoryDropdown(false);
    
    // Reset Collection-Filter wenn History ausgewählt wird
    setSelectedCollection(null);
    setCollectionArticles([]);
    setShowCollectionsDropdown(false);
    
    if (date) {
      await loadHistoricalArticles(date);
    } else {
      setHistoricalArticleIds([]);
    }
  };

  const handleRemoveFromShoppingList = async (article) => {
    try {
      // Finde den Artikel in allen Einkaufslisten
      const { getAllShoppingLists } = await import('../services/shoppingService');
      const result = await getAllShoppingLists();
      
      if (result.success) {
        for (const store of result.data) {
          if (store.items) {
            const itemToRemove = store.items.find(item => item.article_id === article.id);
            if (itemToRemove) {
              // Entferne den Artikel aus der Einkaufsliste
              const { deleteItem } = await import('../services/shoppingService');
              const deleteResult = await deleteItem(itemToRemove.id);
              
              if (deleteResult.success) {
                // Aktualisiere die lokalen States SOFORT
                setItemsInShoppingLists(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(article.id);
                  return newSet;
                });
                // KEIN loadItemsInShoppingLists() Aufruf - State ist bereits korrekt aktualisiert
                return; // Nur den ersten gefundenen Artikel entfernen
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error removing article from shopping list:', error);
      Alert.alert('Fehler', 'Fehler beim Entfernen des Artikels');
    }
  };

  const handleAddArticle = async (article) => {
    // Laden für diesen Artikel bestimmen
    // WICHTIG: Backend-Daten (article.last_used_store_id) haben Priorität über lokale Daten (lastUsedStores)
    const storeId = article.last_used_store_id || lastUsedStores[article.id];
    const selectedStore = storeId ? stores.find(s => s.id === storeId) : null;
    
    // Fallback: Standard-Laden verwenden wenn kein spezifischer Laden gewählt
    const storeToUse = selectedStore || defaultStore;
    
    if (!storeToUse) {
      Alert.alert('Fehler', 'Kein Laden verfügbar');
      return;
    }

    try {
      // WICHTIG: Backend-Daten (article.default_quantity) haben Priorität über lokale Daten (customQuantities)
      // Damit Änderungen in der Einkaufsliste über alle Geräte synchronisiert werden
      const quantity = article.default_quantity || customQuantities[article.id] || 1;
      
      // Tags, Notizen und Einheit direkt aus dem Artikel (Context) verwenden
      const editingData = editingArticle?.id === article.id ? editingArticle : null;
      const tags = editingData?.tags || article.default_tags || [];
      const notes = editingData?.notes || article.notes || null;
      const unit = editingData?.unit || article.default_unit || 'Stück';
      
      const itemData = {
        article_id: article.id,
        quantity: quantity,
        unit: unit,
        tags: tags,
        notes: notes
      };

      // Artikel zum ausgewählten Laden hinzufügen
      const { addItemToList } = await import('../services/shoppingService');
      const result = await addItemToList(storeToUse.id, itemData);
      
      // Artikel immer lokal markieren für sofortiges visuelles Feedback
      // (auch wenn Backend einen Fehler meldet, wird der Artikel meist trotzdem hinzugefügt)
      setAddedItems(prev => new Set([...prev, article.id]));
      setItemsInShoppingLists(prev => new Set([...prev, article.id]));
      
      if (result.success) {
        // Einkaufslisten asynchron neu laden für Synchronisation (nach State-Update)
        setTimeout(() => {
          loadItemsInShoppingLists();
        }, 100);
      } else {
        // Auch bei Fehler: Artikel lokal markieren, da er meist trotzdem hinzugefügt wurde
        
        // Einkaufslisten trotzdem neu laden
        setTimeout(() => {
          loadItemsInShoppingLists();
        }, 100);
      }
    } catch (error) {
      console.error('Error adding article:', error);
      Alert.alert('Fehler', 'Fehler beim Hinzufügen des Artikels');
    }
  };

  const handleChangeStoreForArticle = (article) => {
    // Navigation zur Laden-Auswahl für einen spezifischen Artikel
    const options = [
      {
        text: 'Kein Laden (Standard verwenden)',
        onPress: async () => {
          // Laden-Zuordnung für diesen Artikel entfernen
          const currentStores = { ...lastUsedStores };
          delete currentStores[article.id];
          setLastUsedStores(currentStores);
          await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(currentStores));
        }
      },
      ...stores.map(store => ({
        text: store.name,
        onPress: async () => {
          await saveLastUsedStoreForArticle(article.id, store.id);
        }
      })),
      { text: 'Abbrechen', style: 'cancel' }
    ];

    Alert.alert(
      'Laden wählen',
      `Welchen Laden möchten Sie für "${article.name}" verwenden?`,
      options
    );
  };

  const renderArticle = ({ item }) => {
    const isAddedInSession = addedItems.has(item.id);
    const isInShoppingList = itemsInShoppingLists.has(item.id);
    const isAdded = isAddedInSession || isInShoppingList; // Artikel ist hinzugefügt wenn in Session oder Einkaufsliste
    const isEditing = editingArticle?.id === item.id;
    // WICHTIG: Backend-Daten (item.last_used_store_id) haben Priorität über lokale Daten (lastUsedStores)
    const storeId = item.last_used_store_id || lastUsedStores[item.id];
    const selectedStore = storeId ? stores.find(s => s.id === storeId) : null;
    
    
    // WICHTIG: Backend-Daten (item.default_quantity) haben Priorität über lokale Daten (customQuantities)
    const displayQuantity = item.default_quantity || customQuantities[item.id] || 1;
    
    return (
      <View style={[styles.articleCard, isAdded && styles.articleCardAdded]}>
        <View style={styles.articleContent}>
          <View style={styles.articleMainRow}>
            <View style={styles.articleNameContainer}>
              <View style={styles.articleNameRow}>
                <Text style={[styles.articleName, isAdded && styles.articleNameAdded]}>
                  {item.name}
                </Text>
                {item.image_url && (
                  <TouchableOpacity
                    style={styles.imageIconButton}
                    onPress={() => {
                      setSelectedImageUrl(item.image_url);
                      setShowImageModal(true);
                    }}
                  >
                    <Ionicons name="image" size={16} color="#666" />
                  </TouchableOpacity>
                )}
              </View>
              {(() => {
                const displayTags = (item.default_tags || []).filter(tag => tag && tag.trim() !== '');
                return displayTags.length > 0 && (
                  <View style={styles.tagsContainer}>
                    {displayTags.slice(0, 2).map((tag, index) => (
                      <View key={index} style={styles.tag}>
                        <Text style={styles.tagText}>{tag.trim()}</Text>
                      </View>
                    ))}
                  </View>
                );
              })()}
            </View>
          </View>
          
          {item.notes && item.notes.trim() !== '' && (
            <View style={styles.articleFooter}>
              <Text style={styles.articleNotes} numberOfLines={1}>
                {item.notes.trim()}
              </Text>
            </View>
          )}
        </View>
        
        <View style={styles.articleActions}>
          {/* Laden-Auswahl */}
          <TouchableOpacity
            style={[styles.storeButton, isEditing && styles.storeButtonEditing]}
            onPress={() => {
              setEditingArticle({
                id: item.id,
                storeId: storeId,
                quantity: displayQuantity?.toString() || '1',
                unit: item.default_unit || 'Stück',
                tags: [...(item.default_tags || [])],
                notes: item.notes || '',
                imageUrl: item.image_url || '',
                uploadingImage: false
              });
              setNewTagText('');
            }}
          >
            <Text style={styles.storeButtonText} numberOfLines={1}>
              {selectedStore ? selectedStore.name : 'Laden wählen'}
            </Text>
            <Ionicons name="chevron-down" size={12} color="#666" />
          </TouchableOpacity>
          
          {/* Menge */}
          <TouchableOpacity
            style={[styles.quantityButton, isEditing && styles.quantityButtonEditing]}
            onPress={() => {
              setEditingArticle({
                id: item.id,
                storeId: storeId,
                quantity: displayQuantity?.toString() || '1',
                unit: item.default_unit || 'Stück',
                tags: [...(item.default_tags || [])],
                notes: item.notes || '',
                imageUrl: item.image_url || '',
                uploadingImage: false
              });
              setNewTagText('');
            }}
          >
            <View style={styles.quantityContainer}>
              <Text style={styles.quantityText}>
                {formatQuantity(displayQuantity)}
              </Text>
              <Text style={styles.unitText}>
                {item.default_unit || 'Stück'}
              </Text>
            </View>
            <Ionicons name="chevron-down" size={12} color="#666" />
          </TouchableOpacity>
          
          {/* Hinzufügen/Entfernen-Button */}
          <TouchableOpacity
            style={[styles.addButton, isAdded && styles.addButtonAdded]}
            onPress={() => {
              if (isAdded) {
                // Artikel entfernen - immer aus beiden Listen entfernen
                if (isAddedInSession) {
                  // Aus Session-Liste entfernen
                  setAddedItems(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(item.id);
                    return newSet;
                  });
                }
                
                if (isInShoppingList) {
                  // Aus Einkaufsliste entfernen (Backend-Call)
                  handleRemoveFromShoppingList(item);
                }
              } else {
                // Artikel hinzufügen
                handleAddArticle(item);
              }
            }}
          >
            {isAdded ? (
              <Ionicons name="checkmark-circle" size={24} color="#4CAF50" />
            ) : (
              <Ionicons name="add-circle-outline" size={24} color="#2E7D32" />
            )}
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // Bild-Upload-Funktionen
  const handleImageSelection = async (source) => {
    setEditingArticle(prev => ({ ...prev, uploadingImage: true }));
    
    try {
      const result = await selectAndUploadImage(source);
      
      if (result.success) {
        setEditingArticle(prev => ({ ...prev, imageUrl: result.imageUrl, uploadingImage: false }));
      } else {
        setEditingArticle(prev => ({ ...prev, uploadingImage: false }));
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      console.error('Image selection error:', error);
      setEditingArticle(prev => ({ ...prev, uploadingImage: false }));
      Alert.alert('Fehler', 'Fehler beim Hochladen des Bildes: ' + error.message);
    }
  };

  const showImageOptions = () => {
    // Für Web: Direkt Galerie öffnen, da Kamera im Web nicht funktioniert
    if (typeof window !== 'undefined' && window.navigator) {
      handleImageSelection('gallery');
      return;
    }
    
    Alert.alert(
      'Bild auswählen',
      'Woher möchtest du das Bild laden?',
      [
        { text: 'Abbrechen', style: 'cancel' },
        { text: 'Kamera', onPress: () => handleImageSelection('camera')},
        { text: 'Galerie', onPress: () => handleImageSelection('gallery')},
      ]
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
        <Text style={styles.loadingText}>Lade Artikel...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Suchleiste */}
      <View style={[styles.searchContainer, { zIndex: 999 }]}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search-outline" size={20} color="#666" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Suche"
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#999"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchQuery('')}
              style={styles.clearButton}
            >
              <Ionicons name="close-circle" size={20} color="#666" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Filter-Zeile: Körbe und Historie */}
      <View style={[styles.filterContainer, { zIndex: 998 }]}>
        <View style={styles.filterRow}>
          {/* Collections-Filter */}
          <View style={styles.collectionsFilterContainer}>
            <TouchableOpacity
              style={[
                styles.collectionsFilterButton,
                selectedCollection && styles.collectionsFilterButtonActive
              ]}
              onPress={(e) => {
                if (typeof e?.stopPropagation === 'function') {
                  e.stopPropagation();
                }
                if (collections.length > 0) {
                  setShowCollectionsDropdown(!showCollectionsDropdown);
                }
              }}
            >
              <Ionicons 
                name={selectedCollection ? "basket" : "basket-outline"} 
                size={20} 
                color={selectedCollection ? "#fff" : "#666"} 
              />
              <Text style={[
                styles.collectionsFilterText,
                selectedCollection && styles.collectionsFilterTextActive
              ]}>
                {selectedCollection ? selectedCollection.name : 'Körbe'}
              </Text>
              <Ionicons 
                name={showCollectionsDropdown ? "chevron-up" : "chevron-down"} 
                size={16} 
                color={selectedCollection ? "#fff" : "#666"} 
              />
            </TouchableOpacity>
            
            {/* Collections-Dropdown */}
            {showCollectionsDropdown && (
              <ScrollView 
                style={styles.collectionsDropdown} 
                nestedScrollEnabled={true}
                onStartShouldSetResponder={() => true}
              >
                <TouchableOpacity
                  style={styles.collectionsDropdownItem}
                  onPress={(e) => {
                    if (typeof e?.stopPropagation === 'function') {
                      e.stopPropagation();
                    }
                    handleCollectionSelect(null);
                  }}
                >
                  <Text style={[
                    styles.collectionsDropdownText,
                    !selectedCollection && styles.collectionsDropdownTextActive
                  ]}>
                    Alle Artikel
                  </Text>
                </TouchableOpacity>
                
                {collections.map((collection) => (
                  <TouchableOpacity
                    key={collection.id}
                    style={styles.collectionsDropdownItem}
                    onPress={(e) => {
                      if (typeof e?.stopPropagation === 'function') {
                        e.stopPropagation();
                      }
                      handleCollectionSelect(collection);
                    }}
                  >
                    <Text style={[
                      styles.collectionsDropdownText,
                      selectedCollection?.id === collection.id && styles.collectionsDropdownTextActive
                    ]}>
                      {collection.name} ({collection.item_count})
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
          </View>
          
          {/* Historischer Datums-Filter */}
          <View style={styles.historyFilterContainer}>
          <TouchableOpacity
            style={[
              styles.historyFilterButton,
              selectedHistoryDate && styles.historyFilterButtonActive
            ]}
            onPress={(e) => {
              if (typeof e?.stopPropagation === 'function') {
                e.stopPropagation();
              }
              if (historicalDates.length > 0) {
                setShowHistoryDropdown(!showHistoryDropdown);
              }
            }}
          >
            <Ionicons 
              name={selectedHistoryDate ? "time" : "time-outline"} 
              size={20} 
              color={selectedHistoryDate ? "#fff" : "#666"} 
            />
            <Text style={[
              styles.historyFilterText,
              selectedHistoryDate && styles.historyFilterTextActive
            ]}>
              {selectedHistoryDate 
                ? new Date(selectedHistoryDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
                : 'Historie'
              }
            </Text>
            <Ionicons 
              name={showHistoryDropdown ? "chevron-up" : "chevron-down"} 
              size={16} 
              color={selectedHistoryDate ? "#fff" : "#666"} 
            />
          </TouchableOpacity>
          
          {/* Dropdown-Menü */}
          {showHistoryDropdown && (
            <ScrollView 
              style={styles.historyDropdown} 
              nestedScrollEnabled={true}
              onStartShouldSetResponder={() => true}
            >
              <TouchableOpacity
                style={styles.historyDropdownItem}
                onPress={(e) => {
                  if (typeof e?.stopPropagation === 'function') {
                    e.stopPropagation();
                  }
                  handleHistorySelect(null);
                }}
              >
                <Text style={[
                  styles.historyDropdownText,
                  !selectedHistoryDate && styles.historyDropdownTextActive
                ]}>
                  Alle Artikel
                </Text>
              </TouchableOpacity>
              
              {historicalDates.map((dateEntry) => (
                <TouchableOpacity
                  key={dateEntry.purchase_date}
                  style={styles.historyDropdownItem}
                  onPress={(e) => {
                    if (typeof e?.stopPropagation === 'function') {
                      e.stopPropagation();
                    }
                    handleHistorySelect(dateEntry.purchase_date);
                  }}
                >
                  <Text style={[
                    styles.historyDropdownText,
                    selectedHistoryDate === dateEntry.purchase_date && styles.historyDropdownTextActive
                  ]}>
                    {new Date(dateEntry.purchase_date).toLocaleDateString('de-DE', { 
                      day: '2-digit', 
                      month: '2-digit',
                      year: 'numeric'
                    })} ({dateEntry.item_count} Artikel)
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
          </View>
        </View>
      </View>

      <FlatList
        data={filteredArticles}
        renderItem={renderArticle}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl 
            refreshing={refreshing} 
            onRefresh={onRefresh}
            colors={['#2E7D32']}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="list-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>
              {searchQuery ? 'Keine Artikel gefunden' : 'Keine Artikel vorhanden'}
            </Text>
            {searchQuery ? (
              <>
                <Text style={styles.emptySubtext}>
                  Möchtest du einen neuen Artikel erstellen?
                </Text>
                <TouchableOpacity
                  style={styles.createArticleButton}
                  onPress={createArticleFromSearch}
                >
                  <Ionicons name="add-circle-outline" size={20} color="#fff" />
                  <Text style={styles.createArticleButtonText}>
                    "{searchQuery}" erstellen
                  </Text>
                </TouchableOpacity>
              </>
            ) : (
              <Text style={styles.emptySubtext}>
                Erstellen Sie zuerst Artikel im "Artikel"-Tab
              </Text>
            )}
          </View>
        }
        ListFooterComponent={
          searchQuery && searchQuery.trim() ? (
            <View style={styles.createArticleFooter}>
              <TouchableOpacity
                style={styles.createArticleFooterButton}
                onPress={createArticleFromSearch}
              >
                <Ionicons name="add-circle-outline" size={20} color="#2E7D32" />
                <Text style={styles.createArticleFooterText}>
                  "{searchQuery}" als neuen Artikel erstellen
                </Text>
              </TouchableOpacity>
            </View>
          ) : null
        }
      />

      {/* Bearbeitungs-Modal */}
      <Modal
        visible={editingArticle !== null}
        transparent
        animationType="slide"
        onRequestClose={() => setEditingArticle(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {editingArticle && (
              <>
                {/* Artikelname - editierbar bei neuen Artikeln */}
                {editingArticle.isNew ? (
                  <TextInput
                    style={styles.modalTitleInput}
                    value={editingArticle.name}
                    onChangeText={(text) => setEditingArticle({...editingArticle, name: text})}
                    placeholder="Artikelname"
                    autoFocus
                  />
                ) : (
                  <Text style={styles.modalTitle}>
                    {articles.find(a => a.id === editingArticle.id)?.name}
                  </Text>
                )}

                    {/* Laden-Auswahl */}
                    <View style={styles.modalSection}>
                      <Text style={styles.modalLabel}>Laden:</Text>
                      <TouchableOpacity
                        style={styles.storeSelectButton}
                        onPress={() => setShowStoreModal(true)}
                      >
                        <Text style={styles.storeSelectText}>
                          {editingArticle.storeId ? stores.find(s => s.id === editingArticle.storeId)?.name || 'Laden wählen' : 'Kein Laden (Standard)'}
                        </Text>
                        <Ionicons name="chevron-down" size={20} color="#666" />
                      </TouchableOpacity>
                    </View>

                {/* Menge-Eingabe */}
                <View style={styles.modalSection}>
                  <View style={styles.quantityLabelRow}>
                    <Text style={styles.modalLabel}>Menge:</Text>
                    <TextInput
                      style={styles.unitInput}
                      value={editingArticle.unit}
                      onChangeText={(text) => setEditingArticle({...editingArticle, unit: text})}
                      placeholder="Einheit"
                    />
                  </View>
                  <View style={styles.quantityInputContainer}>
                    {/* Mengeneingabe */}
                    <TextInput
                      style={styles.quantityInput}
                      value={editingArticle.quantity?.toString() || ''}
                      onChangeText={(text) => {
                        // Erlaube beliebigen Text für Menge (z.B. "2-3", "ca. 500g", etc.)
                        setEditingArticle({...editingArticle, quantity: text});
                      }}
                      placeholder="z.B. 2, 2-3, ca. 500"
                    />
                    {/* Schnelleingabe-Buttons */}
                    <View style={styles.quickInputButtons}>
                      {[1, 2, 3, 4, 5, 6].map(number => (
                        <TouchableOpacity
                          key={number}
                          style={styles.quickInputButton}
                          onPress={() => setEditingArticle({...editingArticle, quantity: number.toString()})}
                        >
                          <Text style={styles.quickInputButtonText}>{number}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                </View>

                {/* Tags-Eingabe */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLabel}>Tags:</Text>
                  <View style={styles.tagsInputContainer}>
                    {editingArticle && editingArticle.tags.map((tag, index) => (
                      <View key={index} style={styles.tagInputItem}>
                        <Text style={styles.tagInputText}>{tag}</Text>
                        <TouchableOpacity
                          style={styles.tagRemoveButton}
                          onPress={() => {
                            const newTags = editingArticle.tags.filter((_, i) => i !== index);
                            setEditingArticle({...editingArticle, tags: newTags});
                          }}
                        >
                          <Ionicons name="close" size={16} color="#666" />
                        </TouchableOpacity>
                      </View>
                    ))}
                    <TextInput
                      style={styles.tagInputField}
                      placeholder="Tag hinzufügen..."
                      value={newTagText}
                      onChangeText={(text) => {
                        setNewTagText(text);
                        if (text.includes(',') || text.includes(' ')) {
                          const newTags = text.split(/[,\s]+/).map(tag => tag.trim()).filter(tag => tag.length > 0);
                          setEditingArticle({
                            ...editingArticle, 
                            tags: [...editingArticle.tags, ...newTags]
                          });
                          setNewTagText('');
                        }
                      }}
                      onSubmitEditing={(event) => {
                        const text = event.nativeEvent.text.trim();
                        if (text.length > 0) {
                          setEditingArticle({
                            ...editingArticle, 
                            tags: [...editingArticle.tags, text]
                          });
                          setNewTagText('');
                        }
                      }}
                      multiline={false}
                      autoCapitalize="words"
                      autoCorrect={false}
                    />
                  </View>
                </View>

                {/* Notizen-Eingabe */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLabel}>Notizen:</Text>
                  <TextInput
                    style={[styles.textInput, styles.notesInput]}
                    value={editingArticle.notes}
                    onChangeText={(text) => setEditingArticle({...editingArticle, notes: text})}
                    placeholder="z.B. Besonders reif, Marke X bevorzugt"
                    multiline={true}
                    numberOfLines={3}
                  />
                </View>

                {/* Buttons */}
                <View style={styles.modalButtons}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.cancelButton]}
                    onPress={() => {
                      setEditingArticle(null);
                      setNewTagText('');
                    }}
                  >
                    <Text style={styles.cancelButtonText}>Abbrechen</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.modalButton, styles.saveButton]}
                    onPress={async () => {
                      // Tags zusammenstellen
                      let finalTags = [...(editingArticle.tags || [])];
                      if (newTagText.trim()) {
                        finalTags.push(newTagText.trim());
                      }
                      
                      // Prüfen, ob es ein neuer Artikel ist
                      if (editingArticle.isNew) {
                        try {
                          // Neuen Artikel erstellen
                          // WICHTIG: Laden ZWINGEND beim Erstellen setzen, damit article_store_positions erstellt wird
                          // Wenn kein Laden ausgewählt wurde, verwende den ersten verfügbaren Laden
                          const selectedStore = editingArticle.storeId ? stores.find(s => s.id === editingArticle.storeId) : null;
                          const storeToUse = selectedStore || defaultStore || (stores.length > 0 ? stores[0] : null);
                          
                          if (!storeToUse) {
                            Alert.alert('Fehler', 'Kein Laden verfügbar. Bitte erst einen Laden anlegen.');
                            return;
                          }
                          
                          const { createArticle } = await import('../services/articlesService');
                          const result = await createArticle({
                            name: editingArticle.name,
                            default_quantity: editingArticle.quantity,
                            default_unit: editingArticle.unit,
                            default_tags: finalTags,
                            notes: editingArticle.notes,
                            image_url: editingArticle.imageUrl || '',
                            last_used_store_id: storeToUse.id // ZWINGEND setzen, nie null!
                          });

                          if (result.success) {
                            const newArticle = result.data;
                            
                            // Neuen Artikel sofort zum Context hinzufügen
                            addArticle(newArticle);
                            
                            // Laden lokal speichern (für lokale Verwendung)
                            if (storeToUse) {
                              await saveLastUsedStoreForArticle(newArticle.id, storeToUse.id);
                              
                              // Direkt zur Einkaufsliste hinzufügen
                              try {
                                const { addItemToList } = await import('../services/shoppingService');
                                const itemData = {
                                  article_id: newArticle.id,
                                  quantity: editingArticle.quantity || 1,
                                  unit: editingArticle.unit || 'Stück',
                                  tags: finalTags || [],
                                  notes: editingArticle.notes || null
                                };
                                
                                const addResult = await addItemToList(storeToUse.id, itemData);
                                
                                if (addResult.success) {
                                  // Markiere als hinzugefügt
                                  setAddedItems(prev => new Set([...prev, newArticle.id]));
                                  setItemsInShoppingLists(prev => new Set([...prev, newArticle.id]));
                                  
                                  // Einkaufslisten neu laden für Synchronisation
                                  setTimeout(() => {
                                    loadItemsInShoppingLists();
                                  }, 100);
                                } else {
                                  console.error('❌ Fehler beim Hinzufügen zur Einkaufsliste:', addResult.error);
                                }
                              } catch (addError) {
                                console.error('❌ Fehler beim Hinzufügen zur Einkaufsliste:', addError);
                              }
                            }
                            
                            // Artikel manuell laden, um sofortige Anzeige zu gewährleisten
                            await loadArticles();
                            
                            // Suchfeld leeren, damit der neue Artikel sofort sichtbar ist
                            setSearchQuery('');
                            setEditingArticle(null);
                            setNewTagText('');
                          } else {
                            Alert.alert('Fehler', result.error || 'Fehler beim Erstellen des Artikels');
                          }
                        } catch (error) {
                          console.error('Error creating article:', error);
                          Alert.alert('Fehler', 'Fehler beim Erstellen des Artikels');
                        }
                      } else {
                        // Bestehenden Artikel aktualisieren
                        // Laden speichern
                        if (editingArticle.storeId) {
                          await saveLastUsedStoreForArticle(editingArticle.id, editingArticle.storeId);
                          // State sofort aktualisieren
                          setLastUsedStores(prev => ({
                            ...prev,
                            [editingArticle.id]: editingArticle.storeId
                          }));
                        } else {
                          // Laden-Zuordnung entfernen
                          const currentStores = { ...lastUsedStores };
                          delete currentStores[editingArticle.id];
                          setLastUsedStores(currentStores);
                          await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(currentStores));
                        }
                        
                        // Tags verarbeiten: Bestehende Tags + eventuell noch nicht gespeicherter Text
                        let finalTags = [...editingArticle.tags];
                        if (newTagText.trim()) {
                          finalTags.push(newTagText.trim());
                          setNewTagText(''); // Eingabefeld leeren
                        }

                        // Menge, Tags und Notizen als neue Standardwerte speichern
                        await saveCustomQuantityForArticle(editingArticle.id, editingArticle.quantity);
                        
                        // States aktualisieren
                        setCustomQuantities(prev => ({...prev, [editingArticle.id]: editingArticle.quantity}));
                        
                        // Zentralen Context aktualisieren (EINZIGE Wahrheit!)
                        const article = articles.find(a => a.id === editingArticle.id);
                        if (article) {
                          await updateArticle(editingArticle.id, {
                            name: article.name, // Name ist erforderlich!
                            default_quantity: editingArticle.quantity,
                            default_tags: finalTags,
                            notes: editingArticle.notes,
                            default_unit: editingArticle.unit,
                            last_used_store_id: editingArticle.storeId, // Laden speichern!
                            image_url: editingArticle.imageUrl || ''
                          });
                        }
                        
                        // WICHTIG: Auch in AsyncStorage speichern für Persistenz (Fallback)
                        if (editingArticle.storeId) {
                          await saveLastUsedStoreForArticle(editingArticle.id, editingArticle.storeId);
                        }
                        
                        // Artikel tatsächlich zur Einkaufsliste hinzufügen
                        try {
                          const { addItemToList } = await import('../services/shoppingService');
                          
                          // Bestimme den zu verwendenden Laden
                          let storeToUse;
                          if (editingArticle.storeId) {
                            // Explizit ausgewählter Laden
                            storeToUse = stores.find(s => s.id === editingArticle.storeId);
                          } else if (lastUsedStores[editingArticle.id]) {
                            // Gelernte Ladenzuordnung verwenden
                            storeToUse = stores.find(s => s.id === lastUsedStores[editingArticle.id]);
                          } else {
                            // Fallback zum ersten Laden
                            storeToUse = stores[0];
                          }
                          
                          if (storeToUse) {
                            const itemData = {
                              article_id: editingArticle.id,
                              quantity: editingArticle.quantity,
                              unit: editingArticle.unit,
                              tags: finalTags,
                              notes: editingArticle.notes
                            };
                            
                            const result = await addItemToList(storeToUse.id, itemData);
                            
                            if (result.success) {
                              // Artikel als "hinzugefügt" markieren
                              setAddedItems(prev => new Set([...prev, editingArticle.id]));
                              setItemsInShoppingLists(prev => new Set([...prev, editingArticle.id]));
                            }
                          }
                        } catch (error) {
                          console.error('Fehler beim addItemToList:', error);
                        }
                        
                        setEditingArticle(null);
                        setNewTagText('');
                      }
                    }}
                  >
                    <Text style={styles.saveButtonText}>Speichern</Text>
                  </TouchableOpacity>
                </View>

                {/* Produktbild */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalLabel}>Produktbild</Text>
                  
                  {editingArticle.imageUrl ? (
                    <View style={styles.imageContainer}>
                      <Image source={{ uri: editingArticle.imageUrl }} style={styles.previewImage} />
                      {/* Kleine Icon-Buttons über dem Bild */}
                      <TouchableOpacity
                        style={styles.editImageIcon}
                        onPress={showImageOptions}
                        disabled={editingArticle.uploadingImage}
                      >
                        <Ionicons name="create-outline" size={16} color="#fff" />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.deleteImageIcon}
                        onPress={() => {
                          if (window.confirm('Möchtest du das Bild wirklich löschen?')) {
                            setEditingArticle(prev => ({ ...prev, imageUrl: '' }));
                          }
                        }}
                        disabled={editingArticle.uploadingImage}
                      >
                        <Ionicons name="trash-outline" size={16} color="#fff" />
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={styles.imagePlaceholder}
                      onPress={showImageOptions}
                      disabled={editingArticle.uploadingImage}
                    >
                      {editingArticle.uploadingImage ? (
                        <ActivityIndicator size="small" color="#2E7D32" />
                      ) : (
                        <>
                          <Ionicons name="camera" size={32} color="#ccc" />
                          <Text style={styles.placeholderText}>Bild hinzufügen</Text>
                        </>
                      )}
                    </TouchableOpacity>
                  )}
                  
                  <Text style={styles.hint}>Tippe auf das Feld, um ein Bild aus der Kamera oder Galerie auszuwählen</Text>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>

      {/* Store-Auswahl Modal */}
      <Modal
        visible={showStoreModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStoreModal(false)}
      >
        <View style={styles.storeModalOverlay}>
          <View style={styles.storeModalContent}>
            <Text style={styles.storeModalTitle}>Laden auswählen</Text>
            
            <ScrollView style={styles.storeList}>
              {stores.map(store => {
                const isSelected = editingArticle?.storeId === store.id;
                return (
                  <TouchableOpacity
                    key={store.id}
                    style={[styles.storeOption, isSelected && styles.storeOptionSelected]}
                    onPress={() => {
                      setEditingArticle({...editingArticle, storeId: store.id});
                      setShowStoreModal(false);
                    }}
                  >
                    <Text style={[styles.storeOptionText, isSelected && styles.storeOptionTextSelected]}>
                      {store.name}
                    </Text>
                    {isSelected && (
                      <Ionicons name="checkmark" size={20} color="#2E7D32" />
                    )}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
            
            <TouchableOpacity
              style={styles.storeModalCloseButton}
              onPress={() => setShowStoreModal(false)}
            >
              <Text style={styles.storeModalCloseText}>Abbrechen</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Bild-Anzeige-Modal */}
      <Modal
        visible={showImageModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowImageModal(false)}
      >
        <View style={styles.imageModalOverlay}>
          <View style={styles.imageModalContent}>
            <TouchableOpacity
              style={styles.imageModalCloseButton}
              onPress={() => setShowImageModal(false)}
            >
              <Ionicons name="close" size={24} color="#fff" />
            </TouchableOpacity>
            {selectedImageUrl && (
              <Image
                source={{ uri: selectedImageUrl }}
                style={styles.imageModalImage}
                resizeMode="contain"
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  listContent: {
    padding: 6,
  },
  articleCard: {
    backgroundColor: '#fff',
    borderRadius: 6,
    padding: 6,
    marginBottom: 4,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  articleCardAdded: {
    backgroundColor: '#f0f7f0',
    borderColor: '#C8E6C9',
  },
  articleContent: {
    flex: 1,
  },
  articleMainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  articleNameContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
  },
  articleName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  articleNameAdded: {
    color: '#4CAF50',
  },
  articleDetails: {
    alignItems: 'flex-end',
  },
  articleQuantity: {
    fontSize: 12,
    color: '#666',
  },
  purchaseCount: {
    fontSize: 10,
    color: '#999',
    marginTop: 2,
  },
  articleFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  articleNotes: {
    fontSize: 10,
    color: '#999',
    fontStyle: 'italic',
    flex: 1,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginLeft: 6,
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
    marginRight: 3,
  },
  tagText: {
    fontSize: 9,
    color: '#2E7D32',
    fontWeight: '600',
  },
  articleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8,
    flexWrap: 'wrap',
    gap: 4,
  },
  storeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 6, // Noch etwas reduziert
    borderRadius: 4,
    marginRight: 4,
    minWidth: 80,
    maxWidth: 120,
    height: 32, // Etwas niedrigere Höhe
  },
  storeButtonText: {
    fontSize: 10,
    color: '#666',
    marginRight: 2,
    flex: 1,
  },
  addButton: {
    padding: 8,
  },
  addButtonAdded: {
    backgroundColor: '#E8F5E9',
    borderRadius: 20,
  },
  quantityButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginRight: 4,
    minWidth: 50,
    maxWidth: 80,
  },
  quantityButtonEditing: {
    backgroundColor: '#E8F5E9',
  },
  quantityContainer: {
    alignItems: 'center',
    marginRight: 2,
  },
  quantityText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    lineHeight: 14,
  },
  unitText: {
    fontSize: 8,
    color: '#666',
    lineHeight: 10,
  },
  storeButtonEditing: {
    backgroundColor: '#E8F5E9',
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  modalTitleInput: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: '#2E7D32',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f9f9f9',
  },
  modalSection: {
    marginBottom: 20,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  storeOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  storeOption: {
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  storeOptionSelected: {
    backgroundColor: '#E8F5E9',
    borderColor: '#2E7D32',
  },
  storeOptionText: {
    fontSize: 12,
    color: '#666',
  },
  storeOptionTextSelected: {
    color: '#2E7D32',
    fontWeight: '600',
  },
  quantityInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  quantityInput: {
    width: 60,
    minWidth: 60,
    fontSize: 20,
    fontWeight: '700',
    color: '#2E7D32',
    textAlign: 'center',
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: '#2E7D32',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  quantityUnit: {
    fontSize: 14,
    color: '#666',
    marginLeft: 8,
  },
  textInput: {
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  notesInput: {
    height: 80,
    textAlignVertical: 'top',
  },
  tagsInputContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  tagInputItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 4,
    marginBottom: 4,
  },
  tagInputText: {
    fontSize: 12,
    color: '#2E7D32',
    marginRight: 4,
  },
  tagRemoveButton: {
    padding: 2,
  },
  tagInputField: {
    flex: 1,
    minWidth: 120,
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  modalButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: '#2E7D32',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 60,
  },
  emptyText: {
    fontSize: 20,
    color: '#999',
    marginTop: 20,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 10,
    textAlign: 'center',
  },
  createArticleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2E7D32',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 20,
    gap: 8,
  },
  createArticleButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  searchContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  filterContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    minWidth: 0,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 0,
    minWidth: 0,
    outline: 'none',
    borderWidth: 0,
  },
  clearButton: {
    marginLeft: 8,
    padding: 2,
  },
  quickInputButtons: {
    flexDirection: 'row',
    gap: 4,
  },
  quickInputButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  quickInputButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  quantityInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  quantityLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  quantityUnitLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  unitInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    color: '#333',
    backgroundColor: '#f9f9f9',
  },
  historyFilterContainer: {
    flex: 1,
    position: 'relative',
    marginLeft: 6,
    zIndex: 1000,
  },
  historyFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 4,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    cursor: 'pointer',
  },
  historyFilterButtonActive: {
    backgroundColor: '#2E7D32',
    borderColor: '#2E7D32',
  },
  historyFilterText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '600',
    userSelect: 'none',
  },
  historyFilterTextActive: {
    color: '#fff',
  },
  historyDropdown: {
    position: 'absolute',
    top: 42,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 10,
    zIndex: 10000,
    maxHeight: 300,
    overflow: 'auto',
  },
  historyDropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    cursor: 'pointer',
  },
  historyDropdownText: {
    fontSize: 14,
    color: '#333',
    userSelect: 'none',
  },
  historyDropdownTextActive: {
    color: '#2E7D32',
    fontWeight: '600',
  },
  storeSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  storeSelectText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  storeModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  storeModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '80%',
    maxHeight: '70%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  storeModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 15,
    textAlign: 'center',
  },
  storeList: {
    maxHeight: 300,
  },
  storeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderRadius: 8,
    marginBottom: 5,
    backgroundColor: '#f5f5f5',
  },
  storeOptionSelected: {
    backgroundColor: '#E8F5E9',
    borderWidth: 1,
    borderColor: '#2E7D32',
  },
  storeOptionText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  storeOptionTextSelected: {
    color: '#2E7D32',
    fontWeight: '600',
  },
  storeModalCloseButton: {
    backgroundColor: '#f5f5f5',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginTop: 15,
    alignItems: 'center',
  },
  storeModalCloseText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  // Bild-Upload Styles
  imageContainer: {
    alignItems: 'center',
    marginBottom: 10,
    position: 'relative',
  },
  previewImage: {
    width: 120,
    height: 120,
    borderRadius: 8,
    marginBottom: 10,
  },
  editImageIcon: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 0,
    borderBottomLeftRadius: 8,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteImageIcon: {
    position: 'absolute',
    bottom: 10,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 0,
    borderBottomRightRadius: 8,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholder: {
    width: 120,
    height: 120,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#e0e0e0',
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  placeholderText: {
    color: '#999',
    marginTop: 8,
    fontSize: 12,
    textAlign: 'center',
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 5,
    textAlign: 'center',
  },
  // Bild-Icon und Modal Styles
  articleNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  imageIconButton: {
    padding: 4,
    marginLeft: 8,
  },
  imageModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalContent: {
    width: '90%',
    height: '80%',
    position: 'relative',
  },
  imageModalCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 20,
    padding: 8,
  },
  imageModalImage: {
    width: '100%',
    height: '100%',
  },
  // Collections-Filter Styles
  collectionsFilterContainer: {
    flex: 1,
    position: 'relative',
    marginRight: 6,
  },
  collectionsFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    minWidth: 100,
  },
  collectionsFilterButtonActive: {
    backgroundColor: '#2E7D32',
  },
  collectionsFilterText: {
    fontSize: 14,
    color: '#666',
    marginLeft: 6,
    marginRight: 6,
    flexShrink: 1,
  },
  collectionsFilterTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  collectionsDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    maxHeight: 200,
    zIndex: 1000,
    marginTop: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  collectionsDropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  collectionsDropdownText: {
    fontSize: 14,
    color: '#333',
  },
  collectionsDropdownTextActive: {
    color: '#2E7D32',
    fontWeight: '600',
  },
  createArticleFooter: {
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  createArticleFooterButton: {
    backgroundColor: '#fff',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#2E7D32',
  },
  createArticleFooterText: {
    color: '#2E7D32',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
});

export default QuickListScreen;
